import React, { useEffect, useState } from "react";
import { FetchKeyMetrics } from "../../../Services/api";
import { useDispatch, useSelector } from "react-redux";
import {
  IconButton,
  Skeleton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TablePagination,
  TableRow,
  Button,
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { utils, writeFile } from "xlsx";
import { canExportToCSV } from "../config";
import DownloadIcon from "@mui/icons-material/Download";

const KeyMetrics = () => {
  const dispatch = useDispatch();
  const keyMetrics = useSelector((state) => state.keyMetrics) || [];
  const page = useSelector((state) => state.SetPage);
  const rowsPerPage = useSelector((state) => state.RowsPerPage);
  const searchItem = useSelector((state) => state.SearchItems);
  const selectedExchange = useSelector((state) => state.SetExchange);
  const isLoading = useSelector((state) => state.isLoading);

  const [metricsData, setMetricsData] = useState([]);

  useEffect(() => {
    FetchKeyMetrics(dispatch, selectedExchange);
  }, [dispatch, selectedExchange]);

  useEffect(() => {
    if (selectedExchange === "NSE") {
      setMetricsData(keyMetrics?.data?.nseScrips || []);
    } else {
      setMetricsData(keyMetrics?.data?.bseScrips || []);
    }
  }, [keyMetrics, selectedExchange]);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#ebebeb",
      color: theme.palette.common.black,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      minWidth: "120px",
      borderRight: `1px solid ${theme.palette.divider}`,
    },
  }));
  const StyledTableRow = styled(TableRow)(() => ({}));

  // const StyledTableRow = styled(TableRow)(() => ({
  //   "&:last-child td, &:last-child th": {
  //     border: 0,
  //   },
  // }));

  const currentSearchTerm =
    searchItem?.length > 0 ? searchItem[searchItem.length - 1] : "";

  const filterTicker = currentSearchTerm
    ? metricsData.filter((ele) =>
        String(ele[1])?.toLowerCase().includes(currentSearchTerm.toLowerCase())
      )
    : metricsData;

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayedData = filterTicker?.slice(startIndex, endIndex);

  const handleChangePage = (event, newPage) => {
    dispatch({ type: "SET_PAGE", payload: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: "ROWS_PER_PAGE",
      payload: parseInt(event.target.value, 10),
    });
  };

  const handleExportCSV = () => {
    const headers = [
      "symbol",
      "stBeta",
      "mtBeta",
      "ltBeta",
      "annualizedVolatility",
      "stMaxDdn",
      "mtMaxDdn",
      "ltMaxDdn",
      "annualizedreturn3yr",
      "annualizedreturn5yr",
      "stAlpha",
      "mtAlpha",
      "ltAlpha",
      "stSharpeRatio",
      "mtSharpeRatio",
      "ltSharpeRatio",
      "stSortinoRatio",
      "mtSortinoRatio",
      "ltSortinoRatio",
      "stInformationRatio",
      "mtInformationRatio",
      "ltInformationRatio",
    ];

    const csvData = metricsData.map((row) => {
      const rowData = {};
      rowData["symbol"] = row[1]; // Symbol
      for (let i = 4; i < headers.length + 4; i++) {
        rowData[headers[i - 3]] = row[i];
      }
      return rowData;
    });

    const ws = utils.json_to_sheet(csvData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "KeyMetrics");
    writeFile(wb, "KeyMetrics.xlsx");
  };

  const TablePaginationActions = ({
    count,
    page,
    rowsPerPage,
    onPageChange,
  }) => (
    <div style={{ flexShrink: 0, marginLeft: "2.5em" }}>
      <IconButton
        onClick={(event) => onPageChange(event, 0)}
        disabled={page === 0}
      >
        <FirstPageIcon />
      </IconButton>
      <IconButton
        onClick={(event) => onPageChange(event, page - 1)}
        disabled={page === 0}
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={(event) => onPageChange(event, page + 1)}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={(event) =>
          onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
        }
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        <LastPageIcon />
      </IconButton>
    </div>
  );

  return (
    <div>
      {canExportToCSV && (
        <div
          style={{
            display: "flex",
            justifyContent: "end",
            padding: "6px",
            cursor: "pointer",
            position: "static",
          }}
        >
          <DownloadIcon onClick={handleExportCSV} aria-label="export to CSV" />
        </div>
      )}

      {isLoading ? (
        <Stack spacing={1} padding={"5px"}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            animation="wave"
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={50}
            animation="wave"
          />
        </Stack>
      ) : (
        <div style={{ width: "100%", overflowY: "auto" }}>
          <Table stickyHeader aria-label="customized table" size="small">
            <TableHead>
              <TableRow>
                {[
                  "Ticker",
                  "stBeta",
                  "mtBeta",
                  "ltBeta",
                  "annualizedVolatility",
                  "stMaxDdn",
                  "mtMaxDdn",
                  "ltMaxDdn",
                  "annualizedreturn3yr",
                  "annualizedreturn5yr",
                  "stAlpha",
                  "mtAlpha",
                  "ltAlpha",
                  "stSharpeRatio",
                  "mtSharpeRatio",
                  "ltSharpeRatio",
                  "stSortinoRatio",
                  "mtSortinoRatio",
                  "ltSortinoRatio",
                  "stInformationRatio",
                  "mtInformationRatio",
                  "ltInformationRatio",
                ].map((header, idx) => (
                  <StyledTableCell
                    key={idx}
                    sx={{ fontWeight: "bold", textAlign: "center" }}
                  >
                    {header}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedData.map((row, index) => (
                <StyledTableRow key={index}>
                  {[
                    row[1], // Symbol
                    ...row.slice(4, 4 + 21), // 21 metrics from index 4
                  ].map((cell, idx) => (
                    <StyledTableCell
                      key={idx}
                      align={idx === 0 ? "left" : "right"}
                    >
                      {cell}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filterTicker.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />
    </div>
  );
};

export default KeyMetrics;
